import { useEffect, useState } from 'react'
import { Logger } from '@/components/Logger'
import { publicConfig } from '@/config/publicConfig'

interface LatencyIndicatorProps {
  className?: string
}

type LatencyState = 'excellent' | 'good' | 'fair' | 'poor' | 'unknown'

const PING_INTERVAL_MS = 5_000
const PING_TIMEOUT_MS = 10_000

const getLatencyState = (pingTime: number): LatencyState => {
  if (pingTime <= 100) return 'excellent' // 0-100ms
  if (pingTime <= 300) return 'good' // 101-300ms
  if (pingTime <= 600) return 'fair' // 301-600ms
  return 'poor' // 600ms+
}

const measureLatency = async (url: string): Promise<number> => {
  if (typeof fetch === 'undefined') {
    throw new Error('Fetch API is not available in this environment')
  }

  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
  const isAbsoluteUrl = /^https?:\/\//i.test(url)
  const mode: RequestMode = isAbsoluteUrl ? 'no-cors' : 'same-origin'
  const startTime = typeof performance !== 'undefined' ? performance.now() : Date.now()

  let timeoutId: number | undefined

  try {
    if (controller && typeof window !== 'undefined') {
      timeoutId = window.setTimeout(() => controller.abort(), PING_TIMEOUT_MS)
    }

    await fetch(url, {
      method: 'HEAD',
      cache: 'no-store',
      mode,
      signal: controller?.signal,
    })
  } catch (error) {
    if ((error as DOMException)?.name === 'AbortError') {
      throw new Error('Ping request timed out')
    }
    throw error
  } finally {
    if (typeof window !== 'undefined' && timeoutId !== undefined) {
      window.clearTimeout(timeoutId)
    }
  }

  const endTime = typeof performance !== 'undefined' ? performance.now() : Date.now()
  return endTime - startTime
}

const LatencyIndicator = ({ className = '' }: LatencyIndicatorProps) => {
  const [latencyState, setLatencyState] = useState<LatencyState>('unknown')
  const [latency, setLatency] = useState<number | null>(null)
  const apiUrl = publicConfig.api.baseUrl ? `${publicConfig.api.baseUrl}/v1/` : null

  useEffect(() => {
    if (!apiUrl) {
      setLatency(null)
      setLatencyState('unknown')
      return
    }

    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return
    }

    let isCancelled = false
    let isRequestInFlight = false
    let timeoutId: number | null = null

    const clearScheduledPing = () => {
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId)
        timeoutId = null
      }
    }

    const scheduleNextPing = () => {
      clearScheduledPing()

      if (isCancelled || document.hidden) {
        return
      }

      timeoutId = window.setTimeout(() => {
        void performPing()
      }, PING_INTERVAL_MS)
    }

    const performPing = async () => {
      if (isCancelled || isRequestInFlight || document.hidden) {
        return
      }

      isRequestInFlight = true

      try {
        const pingTime = await measureLatency(apiUrl)
        if (isCancelled) {
          return
        }

        const roundedPing = Math.round(pingTime)
        setLatency(roundedPing)
        setLatencyState(getLatencyState(roundedPing))
      } catch (error) {
        if (isCancelled) {
          return
        }

        Logger.warn('Ping failed:', error)
        setLatency(null)
        setLatencyState('unknown')
      } finally {
        isRequestInFlight = false
        scheduleNextPing()
      }
    }

    const handleVisibilityChange = () => {
      if (isCancelled) {
        return
      }

      if (document.hidden) {
        Logger.log('DEBUG LATENCY: Document hidden, clearing scheduled ping')
        clearScheduledPing()
      } else {
        Logger.log('DEBUG LATENCY: Document visible, performing ping')
        void performPing()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    void performPing()

    return () => {
      isCancelled = true
      clearScheduledPing()
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [apiUrl])

  const getArcColor = (arcIndex: number): string => {
    switch (latencyState) {
      case 'excellent':
        return '#10b981' // All 4 arcs green
      case 'good':
        return arcIndex < 3 ? '#10b981' : '#9ca3af' // 3 green, 1 gray
      case 'fair':
        return arcIndex < 2 ? '#eab308' : '#9ca3af' // 2 yellow, 2 gray
      case 'poor':
        return arcIndex === 0 ? '#ef4444' : '#9ca3af' // 1 red, 3 gray
      default:
        return '#9ca3af' // All gray when unknown
    }
  }

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {/* WIFI Symbol */}
      <div className="w-5 h-5">
        <svg viewBox="0 0 24 24" className="w-full h-full">
          {/* Center dot */}
          <circle
            cx="12"
            cy="19"
            r="1.5"
            fill={getArcColor(0)}
            className="transition-colors duration-200"
          />

          {/* Arc 1 - Smallest */}
          <path
            d="M8.5 16.5c1.5-1.5 4.5-1.5 7 0"
            stroke={getArcColor(0)}
            strokeWidth="1.6"
            fill="none"
            strokeLinecap="round"
            className="transition-colors duration-200"
          />

          {/* Arc 2 - Medium */}
          <path
            d="M6 14c3-3 9-3 12 0"
            stroke={getArcColor(1)}
            strokeWidth="1.6"
            fill="none"
            strokeLinecap="round"
            className="transition-colors duration-200"
          />

          {/* Arc 3 - Large */}
          <path
            d="M3.5 11.5c4.5-4.5 13-4.5 17 0"
            stroke={getArcColor(2)}
            strokeWidth="1.6"
            fill="none"
            strokeLinecap="round"
            className="transition-colors duration-200"
          />

          {/* Arc 4 - Largest */}
          <path
            d="M1 9c6-6 16-6 22 0"
            stroke={getArcColor(3)}
            strokeWidth="1.6"
            fill="none"
            strokeLinecap="round"
            className="transition-colors duration-200"
          />
        </svg>
      </div>
      {/* Latency Text */}
      <span className="text-white-dark text-sm">{latency !== null ? `${latency}ms` : '---'}</span>
    </div>
  )
}

export default LatencyIndicator
